import React, { useEffect, useState } from 'react';
import { MegaphoneIcon, ArrowLeftIcon, EditIcon, TrashIcon, InfoIcon, CalendarIcon, TagIcon, DollarSignIcon, PercentIcon, UserIcon, BuildingIcon, CreditCardIcon, BarChart2Icon, LineChartIcon, TrendingUpIcon, PieChartIcon, DownloadIcon, XIcon, CheckIcon } from 'lucide-react';
import { Campaign } from '../types/Campaign';
import { TimeSelector } from '../components/dashboard/TimeSelector';
import { ConversionFunnelChart, ConversionRatePieChart, CodeUsageTimeSeriesChart, PaymentVolumeTimeSeriesChart } from '../components/campaigns/CampaignCharts';
// Mock campaign data - would normally be fetched from API
const mockCampaigns: Record<string, Campaign> = {
  RBLSHOP2025: {
    id: 'RBLSHOP2025',
    title: '[RLife] 7-days Promotion for Shop',
    description: '[RLife] 7-days Promotion for Shop',
    expiryDate: '07/10/2025',
    discountPercentage: 0,
    fixedDiscountAmount: 50,
    currency: 'LKR',
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budget: 5000,
    budgetUsed: 0,
    strictlyEnforceBudget: true,
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'RBLSHOP2025',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 50,
    maxDiscount: 50,
    status: 'active',
    companies: ['Rabbit Life'],
    transactionVolume: 0,
    transactionCount: 0
  },
  BBL10THB: {
    id: 'BBL10THB',
    title: 'BBL Special Package (Rs. 10 Discount)',
    description: 'DISCOUNT_COUPON',
    expiryDate: '02/08/2025',
    discountPercentage: 0,
    fixedDiscountAmount: 10,
    currency: 'LKR',
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budget: 11000,
    budgetUsed: 10960,
    strictlyEnforceBudget: true,
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'BBL10THB',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 10,
    maxDiscount: 10,
    status: 'active'
  }
};
// Tab interface for analytics section
interface AnalyticsTab {
  id: string;
  label: string;
  icon: React.ReactNode;
}
export function CampaignDetails() {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('conversion');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportTimeframe, setExportTimeframe] = useState('30d');
  const [exportStatus, setExportStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const analyticsTabs: AnalyticsTab[] = [{
    id: 'conversion',
    label: 'Conversion',
    icon: <BarChart2Icon size={16} data-id="element-3354" />
  }, {
    id: 'usage',
    label: 'Code Usage',
    icon: <LineChartIcon size={16} data-id="element-3355" />
  }, {
    id: 'volume',
    label: 'Payment Volume',
    icon: <TrendingUpIcon size={16} data-id="element-3356" />
  }];
  useEffect(() => {
    // In a real app, we would fetch the campaign data from an API
    // For now, we'll use the URL parameter to get the campaign ID
    const params = new URLSearchParams(window.location.search);
    const campaignId = params.get('id');
    if (campaignId && mockCampaigns[campaignId]) {
      setCampaign(mockCampaigns[campaignId]);
    }
    setLoading(false);
  }, []);
  const navigateBack = () => {
    window.location.href = '/campaigns';
  };
  const navigateToEdit = () => {
    if (campaign) {
      window.location.href = `/campaign-form?id=${campaign.id}`;
    }
  };
  const handleExport = () => {
    setExportStatus('loading');
    // Simulate export process
    setTimeout(() => {
      setExportStatus('success');
      // Reset after showing success message
      setTimeout(() => {
        setExportStatus('idle');
        setShowExportModal(false);
      }, 2000);
    }, 1000);
  };
  // Calculate budget usage percentage
  const calculateBudgetUsage = () => {
    if (!campaign || campaign.budget === 0) return 0;
    return Math.round(campaign.budgetUsed / campaign.budget * 100);
  };
  if (loading) {
    return <div className="flex justify-center items-center h-64" data-id="element-3357">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" data-id="element-3358"></div>
      </div>;
  }
  if (!campaign) {
    return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-3359">
        <h2 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-3360">
          Campaign not found
        </h2>
        <p className="text-gray-600 mb-4" data-id="element-3361">
          The campaign you are looking for does not exist or has been removed.
        </p>
        <button onClick={navigateBack} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3362">
          Back to Campaigns
        </button>
      </div>;
  }
  // Render analytics content based on active tab
  const renderAnalyticsContent = () => {
    switch (activeTab) {
      case 'conversion':
        return <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-3363">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-3364">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-3365">
                Conversion Funnel
              </h3>
              <ConversionFunnelChart data-id="element-3366" />
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-3367">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-3368">
                Conversion Rates
              </h3>
              <ConversionRatePieChart data-id="element-3369" />
            </div>
          </div>;
      case 'usage':
        return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-3370">
            <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-3371">
              Code Usage Over Time (Last 30 Days)
            </h3>
            <CodeUsageTimeSeriesChart data-id="element-3372" />
          </div>;
      case 'volume':
        return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-3373">
            <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-3374">
              Payment Volume Over Time (Last 30 Days)
            </h3>
            <PaymentVolumeTimeSeriesChart currency={campaign.currency} data-id="element-3375" />
          </div>;
      default:
        return null;
    }
  };
  return <div className="space-y-6" data-id="element-3376">
      {/* Top bar with actions */}
      <div className="flex justify-between items-center" data-id="element-3377">
        <button onClick={navigateBack} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-3378">
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-3379" />
          <span data-id="element-3380">Back to Campaigns</span>
        </button>
        <div className="flex space-x-3" data-id="element-3381">
          <button onClick={() => setShowExportModal(true)} className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center" data-id="element-3382">
            <DownloadIcon size={16} className="mr-2" data-id="element-3383" />
            Export CSV
          </button>
          <button onClick={navigateToEdit} className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center" data-id="element-3384">
            <EditIcon size={16} className="mr-2" data-id="element-3385" />
            Edit Campaign
          </button>
          <button className="px-3 py-2 bg-white border border-red-500 text-red-500 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center" data-id="element-3386">
            <TrashIcon size={16} className="mr-2" data-id="element-3387" />
            Delete
          </button>
        </div>
      </div>
      {/* Export Modal */}
      {showExportModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-3388">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md" data-id="element-3389">
            <div className="flex justify-between items-center mb-4" data-id="element-3390">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-3391">
                Export Campaign Data
              </h3>
              <button onClick={() => {
            setShowExportModal(false);
            setExportStatus('idle');
          }} className="text-gray-400 hover:text-gray-500" data-id="element-3392">
                <XIcon size={20} data-id="element-3393" />
              </button>
            </div>
            <div className="mb-4" data-id="element-3394">
              <p className="text-sm text-gray-600 mb-2" data-id="element-3395">
                Select time period for export:
              </p>
              <TimeSelector value={exportTimeframe} onChange={setExportTimeframe} data-id="element-3396" />
            </div>
            <div className="mb-4" data-id="element-3397">
              <p className="text-sm text-gray-600" data-id="element-3398">
                The export will include all campaign data for the selected
                period:
              </p>
              <ul className="list-disc list-inside mt-2 text-sm text-gray-600" data-id="element-3399">
                <li data-id="element-3400">Daily code usage statistics</li>
                <li data-id="element-3401">Conversion metrics</li>
                <li data-id="element-3402">Transaction volumes and amounts</li>
                <li data-id="element-3403">User engagement data</li>
              </ul>
            </div>
            <div className="flex justify-end" data-id="element-3404">
              {exportStatus === 'idle' && <button onClick={handleExport} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center" data-id="element-3405">
                  <DownloadIcon size={16} className="mr-2" data-id="element-3406" />
                  Export
                </button>}
              {exportStatus === 'loading' && <button disabled className="px-4 py-2 bg-blue-600 text-white rounded-md opacity-70 flex items-center" data-id="element-3407">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" data-id="element-3408"></div>
                  Exporting...
                </button>}
              {exportStatus === 'success' && <button disabled className="px-4 py-2 bg-green-600 text-white rounded-md flex items-center" data-id="element-3409">
                  <CheckIcon size={16} className="mr-2" data-id="element-3410" />
                  Export Complete
                </button>}
              {exportStatus === 'error' && <button onClick={handleExport} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center" data-id="element-3411">
                  Try Again
                </button>}
            </div>
          </div>
        </div>}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-3412">
        <div className="flex items-center mb-6" data-id="element-3413">
          <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mr-4" data-id="element-3414">
            <MegaphoneIcon size={24} className="text-blue-600" data-id="element-3415" />
          </div>
          <div data-id="element-3416">
            <h1 className="text-2xl font-bold text-gray-800" data-id="element-3417">
              {campaign.title}
            </h1>
            <p className="text-gray-500" data-id="element-3418">Campaign ID: {campaign.id}</p>
          </div>
          <div className="ml-auto" data-id="element-3419">
            <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${campaign.status === 'active' ? 'bg-green-100 text-green-800' : campaign.status === 'expired' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-3420">
              {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
            </span>
          </div>
        </div>
        {/* Campaign statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-3421">
          <div className="bg-gray-50 rounded-lg p-4" data-id="element-3422">
            <div className="flex items-center justify-between mb-4" data-id="element-3423">
              <h3 className="text-sm font-medium text-gray-700" data-id="element-3424">
                Total Budget
              </h3>
              <InfoIcon size={16} className="text-gray-400" data-id="element-3425" />
            </div>
            <div className="flex items-center mb-2" data-id="element-3426">
              <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2" data-id="element-3427">
                <div className="bg-blue-600 h-2.5 rounded-full" style={{
                width: `${calculateBudgetUsage()}%`
              }} data-id="element-3428"></div>
              </div>
              <span className="text-sm text-gray-500" data-id="element-3429">
                {calculateBudgetUsage()}%
              </span>
            </div>
            <div className="text-2xl font-bold text-gray-800" data-id="element-3430">
              {campaign.budgetUsed.toLocaleString('en-LK')} /{' '}
              {campaign.budget.toLocaleString('en-LK')} Rs.
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4" data-id="element-3431">
            <div className="flex items-center justify-between mb-4" data-id="element-3432">
              <h3 className="text-sm font-medium text-gray-700" data-id="element-3433">
                Transaction volume for campaign
              </h3>
              <InfoIcon size={16} className="text-gray-400" data-id="element-3434" />
            </div>
            <div className="text-2xl font-bold text-gray-800" data-id="element-3435">
              Rs.{' '}
              {campaign.transactionVolume?.toLocaleString('en-LK') || '0.00'}
            </div>
            <div className="text-xs text-gray-500 mt-1" data-id="element-3436">
              {campaign.transactionCount || 0} transactions have been collected
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4" data-id="element-3437">
            <div className="flex items-center justify-between mb-4" data-id="element-3438">
              <h3 className="text-sm font-medium text-gray-700" data-id="element-3439">KPI</h3>
              <InfoIcon size={16} className="text-gray-400" data-id="element-3440" />
            </div>
            <div className="space-y-4" data-id="element-3441">
              <div data-id="element-3442">
                <p className="text-xs text-gray-500" data-id="element-3443">Transaction volume ROI</p>
                <p className="text-xl font-bold text-gray-800" data-id="element-3444">0</p>
                <p className="text-xs text-gray-500" data-id="element-3445">
                  Against a budget of Rs. 0.00
                </p>
              </div>
              <div data-id="element-3446">
                <p className="text-xs text-gray-500" data-id="element-3447">Transactions</p>
                <p className="text-xl font-bold text-gray-800" data-id="element-3448">0</p>
                <p className="text-xs text-gray-500" data-id="element-3449">0 Merchants</p>
              </div>
            </div>
          </div>
        </div>
        {/* Campaign Analytics Section */}
        <div className="border-t border-gray-200 pt-6 mb-8" data-id="element-3450">
          <div className="flex justify-between items-center mb-6" data-id="element-3451">
            <h2 className="text-lg font-medium text-gray-800 flex items-center" data-id="element-3452">
              <PieChartIcon size={20} className="mr-2 text-blue-600" data-id="element-3453" />
              Campaign Analytics
            </h2>
          </div>
          {/* Analytics Tabs */}
          <div className="mb-6" data-id="element-3454">
            <div className="border-b border-gray-200" data-id="element-3455">
              <nav className="flex -mb-px space-x-8" data-id="element-3456">
                {analyticsTabs.map(tab => <button key={tab.id} className={`py-4 px-1 flex items-center text-sm font-medium ${activeTab === tab.id ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab(tab.id)} data-id="element-3457">
                    <span className="mr-2" data-id="element-3458">{tab.icon}</span>
                    {tab.label}
                  </button>)}
              </nav>
            </div>
          </div>
          {/* Analytics Content */}
          {renderAnalyticsContent()}
        </div>
        {/* Campaign details */}
        <div className="border-t border-gray-200 pt-6" data-id="element-3459">
          <div className="flex justify-between items-center mb-4" data-id="element-3460">
            <h2 className="text-lg font-medium text-gray-800" data-id="element-3461">
              Campaign details
            </h2>
            <button onClick={navigateToEdit} className="text-sm text-blue-600 hover:text-blue-800 flex items-center" data-id="element-3462">
              <EditIcon size={14} className="mr-1" data-id="element-3463" />
              Edit details
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4" data-id="element-3464">
            <div data-id="element-3465">
              <h3 className="text-sm text-gray-500" data-id="element-3466">Title</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3467">
                {campaign.title}
              </p>
            </div>
            <div data-id="element-3468">
              <h3 className="text-sm text-gray-500" data-id="element-3469">Category</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3470">
                {campaign.category}
              </p>
            </div>
            <div data-id="element-3471">
              <h3 className="text-sm text-gray-500" data-id="element-3472">Conditions</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3473">
                {campaign.conditions || 'No information'}
              </p>
            </div>
            <div data-id="element-3474">
              <h3 className="text-sm text-gray-500" data-id="element-3475">Expiry Date</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3476">
                {campaign.expiryDate}
              </p>
            </div>
            <div data-id="element-3477">
              <h3 className="text-sm text-gray-500" data-id="element-3478">Description</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3479">
                {campaign.description}
              </p>
            </div>
            <div data-id="element-3480">
              <h3 className="text-sm text-gray-500" data-id="element-3481">Campaign Owner</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3482">
                {campaign.owner}
              </p>
            </div>
            <div data-id="element-3483">
              <h3 className="text-sm text-gray-500" data-id="element-3484">Code Strategy</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3485">
                {campaign.codeStrategy}
              </p>
            </div>
            <div data-id="element-3486">
              <h3 className="text-sm text-gray-500" data-id="element-3487">Min. Transaction Value</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3488">
                Rs.{' '}
                {campaign.minTransactionValue.toLocaleString('en-LK', {
                minimumFractionDigits: 2
              })}
              </p>
            </div>
            <div data-id="element-3489">
              <h3 className="text-sm text-gray-500" data-id="element-3490">Max. Transaction Value</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3491">
                Rs. {campaign.maxTransactionValue.toLocaleString('en-LK')}
              </p>
            </div>
            <div data-id="element-3492">
              <h3 className="text-sm text-gray-500" data-id="element-3493">Min. Discount</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3494">
                Rs.{' '}
                {campaign.minDiscount.toLocaleString('en-LK', {
                minimumFractionDigits: 2
              })}
              </p>
            </div>
            <div data-id="element-3495">
              <h3 className="text-sm text-gray-500" data-id="element-3496">Max. Discount</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3497">
                Rs.{' '}
                {campaign.maxDiscount.toLocaleString('en-LK', {
                minimumFractionDigits: 2
              })}
              </p>
            </div>
            <div data-id="element-3498">
              <h3 className="text-sm text-gray-500" data-id="element-3499">Valid MCC</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3500">
                {campaign.validMCC?.join(', ') || 'All MCCs'}
              </p>
            </div>
            <div data-id="element-3501">
              <h3 className="text-sm text-gray-500" data-id="element-3502">Valid on days</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3503">
                {campaign.validDays.length === 7 ? 'All days' : campaign.validDays.join(', ')}
              </p>
            </div>
            <div data-id="element-3504">
              <h3 className="text-sm text-gray-500" data-id="element-3505">Buyer Identifier</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3506">
                {campaign.validIdentities?.join(', ') || 'No information'}
              </p>
            </div>
            <div data-id="element-3507">
              <h3 className="text-sm text-gray-500" data-id="element-3508">Valid Identities</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3509">
                {campaign.validIdentities?.join(', ') || 'All identities'}
              </p>
            </div>
            <div data-id="element-3510">
              <h3 className="text-sm text-gray-500" data-id="element-3511">Code</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3512">
                {campaign.code}
              </p>
            </div>
            <div data-id="element-3513">
              <h3 className="text-sm text-gray-500" data-id="element-3514">Currency</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3515">LKR (Rs.)</p>
            </div>
            <div data-id="element-3516">
              <h3 className="text-sm text-gray-500" data-id="element-3517">Discount Percentage</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3518">
                {campaign.discountPercentage}%
              </p>
            </div>
            <div data-id="element-3519">
              <h3 className="text-sm text-gray-500" data-id="element-3520">Fixed discount amount</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3521">
                Rs.{' '}
                {campaign.fixedDiscountAmount.toLocaleString('en-LK', {
                minimumFractionDigits: 2
              })}
              </p>
            </div>
            <div data-id="element-3522">
              <h3 className="text-sm text-gray-500" data-id="element-3523">Discount carried by</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3524">
                {campaign.discountCarriedBy}
              </p>
            </div>
            <div data-id="element-3525">
              <h3 className="text-sm text-gray-500" data-id="element-3526">
                Discount percentage carried by merchant
              </h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3527">
                {campaign.merchantPercentage || 0}%
              </p>
            </div>
            <div data-id="element-3528">
              <h3 className="text-sm text-gray-500" data-id="element-3529">Budget</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3530">
                Rs. {campaign.budget.toLocaleString('en-LK')}
              </p>
            </div>
            <div data-id="element-3531">
              <h3 className="text-sm text-gray-500" data-id="element-3532">Budget used</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3533">
                Rs. {campaign.budgetUsed.toLocaleString('en-LK')}
              </p>
            </div>
            <div data-id="element-3534">
              <h3 className="text-sm text-gray-500" data-id="element-3535">Recurring budget</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3536">
                {campaign.recurringBudget ? `Rs. ${campaign.recurringBudget.toLocaleString('en-LK')}` : 'None'}
              </p>
            </div>
            <div data-id="element-3537">
              <h3 className="text-sm text-gray-500" data-id="element-3538">Recurring budget used</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3539">
                {campaign.recurringBudgetUsed ? `Rs. ${campaign.recurringBudgetUsed.toLocaleString('en-LK')}` : 'None'}
              </p>
            </div>
            <div data-id="element-3540">
              <h3 className="text-sm text-gray-500" data-id="element-3541">Recurring budget period</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3542">
                {campaign.recurringBudgetPeriod || 'No data available'}
              </p>
            </div>
            <div data-id="element-3543">
              <h3 className="text-sm text-gray-500" data-id="element-3544">
                Strictly enforce budget?
              </h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3545">
                {campaign.strictlyEnforceBudget ? 'Yes' : 'No'}
              </p>
            </div>
            <div data-id="element-3546">
              <h3 className="text-sm text-gray-500" data-id="element-3547">Companies</h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3548">
                {campaign.companies?.join(', ') || 'All companies'}
              </p>
            </div>
            <div data-id="element-3549">
              <h3 className="text-sm text-gray-500" data-id="element-3550">
                Limit usage per total customer
              </h3>
              <p className="text-sm font-medium text-gray-800" data-id="element-3551">
                {campaign.limitUsage || 'No information'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}